/**
 * session.ts
 * Client-side auth session: the decoded CLAIMS only — never the token.
 *
 * SECURITY MODEL (httpOnly cookie): the JWT lives in the `suite_session` httpOnly
 * cookie set by the backend. JS — and therefore any XSS payload — CANNOT read it.
 * The browser attaches it automatically on every API call (credentials:'include').
 * The SPA never holds the token; it learns "who am I" from GET /auth/session, which
 * returns the non-sensitive claims (scope, roles, capabilities, exp) the router
 * guard and UI need. Claims live in memory only (a reload re-hydrates from the
 * cookie via hydrateSession()).
 *
 * FS-12: SessionClaims is a discriminated union (TenantClaims | PlatformClaims).
 */
import { reactive, computed, ref, type ComputedRef } from 'vue';
import { getSession, refreshSession } from './api.js';

// ---------------------------------------------------------------------------
// Claim types — discriminated union on `scope`
// ---------------------------------------------------------------------------

export interface TenantClaims {
  scope: 'tenant';
  sub: string;
  tenantId: string;
  roles: string[];
  /** Resolved capability strings embedded at login (AZ-4 / FS-12.1). Absent → []. */
  capabilities: string[];
  exp: number;
}

export interface PlatformClaims {
  scope: 'platform';
  sub: string;
  exp: number;
}

/** Discriminated union: a session is either a tenant session or a platform session. */
export type SessionClaims = TenantClaims | PlatformClaims;

/** The discriminant — useful for guards and computed branching. */
export type SessionScope = SessionClaims['scope'];

interface SessionState {
  claims: SessionClaims | null;
}

// ---------------------------------------------------------------------------
// JWT decode helpers
// ---------------------------------------------------------------------------

/** base64url → JSON. Returns null on any malformed input (never throws). */
export function decodeJwt(token: string): SessionClaims | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeBase64(payload);
    const obj = JSON.parse(json) as Record<string, unknown>;

    // Branch on scope field (FS-12):
    //   scope === 'platform'                          → PlatformClaims
    //   scope === 'tenant' OR (absent + tenantId str) → TenantClaims (back-compat)
    //   anything else                                 → null

    if (obj['scope'] === 'platform') {
      if (typeof obj['sub'] !== 'string' || typeof obj['exp'] !== 'number') return null;
      return { scope: 'platform', sub: obj['sub'], exp: obj['exp'] };
    }

    // Tenant path: explicit scope:'tenant' OR absent scope with tenantId present
    if (obj['scope'] === 'tenant' || (obj['scope'] === undefined && typeof obj['tenantId'] === 'string')) {
      if (
        typeof obj['sub'] !== 'string' ||
        typeof obj['tenantId'] !== 'string' ||
        typeof obj['exp'] !== 'number' ||
        !Array.isArray(obj['roles'])
      ) {
        return null;
      }
      // capabilities: optional in JWT — absent on back-compat tokens → [] (FS-12.1 / AZ-4)
      const rawCaps = obj['capabilities'];
      const capabilities: string[] = Array.isArray(rawCaps)
        ? (rawCaps as unknown[]).filter((c): c is string => typeof c === 'string')
        : [];

      return {
        scope: 'tenant',
        sub: obj['sub'],
        tenantId: obj['tenantId'],
        roles: (obj['roles'] as unknown[]).filter((r): r is string => typeof r === 'string'),
        capabilities,
        exp: obj['exp'],
      };
    }

    return null;
  } catch {
    return null;
  }
}

/** Decode base64 in both browser (atob) and node (Buffer) — tests run in node. */
function decodeBase64(b64: string): string {
  if (typeof atob === 'function') return atob(b64);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (globalThis as any).Buffer.from(b64, 'base64').toString('utf-8');
}

/** True when the token's exp is in the past (with a 10s skew margin). */
export function isExpired(claims: SessionClaims | null): boolean {
  if (!claims) return true;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return claims.exp <= nowSeconds + 10;
}

// --- Singleton reactive session. Starts empty; call hydrateSession() on boot to
// populate it from the httpOnly cookie via GET /auth/session. NOTHING is read from
// or written to Web Storage — the token is never in JS reach. ---

const state = reactive<SessionState>({
  claims: null,
});

// ---------------------------------------------------------------------------
// Account-session (transient) — passes the short-lived account-session token
// from the choose-company login response to selectCompany(), then immediately
// discarded. Mirrored into sessionStorage (2026-07-21): a bare module variable
// does not survive a page reload, and a reload CAN happen mid-flow (PWA
// service-worker auto-update activating, a mobile browser reclaiming a
// backgrounded tab) — for a multi-company account this silently stranded the
// user between "choose your company" and clicking a company, surfacing as
// "Sesión de cuenta expirada" even though nothing was actually wrong. The
// server-side 5-minute TTL on this token already bounds the exposure window,
// so sessionStorage (cleared on tab close, not shared across origins) doesn't
// meaningfully change the risk profile.
// ---------------------------------------------------------------------------

const ACCOUNT_SESSION_STORAGE_KEY = 'suite_account_session';

let accountSessionToken: string | null = null;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Set the in-memory claims directly (e.g. from a GET /auth/session response).
 * Pass null to clear. Does NOT touch any storage — claims are memory-only.
 */
export function setSessionClaims(claims: SessionClaims | null): void {
  state.claims = claims;
}

/**
 * Hydrate the session from the httpOnly cookie by asking the backend who we are.
 * Call once on app boot (before the router's first guard) and after login /
 * select-company / switch-company (the cookie was just (re)set by the backend).
 *
 * Returns true when an authenticated, non-expired session was loaded. On 401 /
 * network error it clears the session and returns false (→ guard bounces to login).
 */
export async function hydrateSession(): Promise<boolean> {
  try {
    const claims = await getSession();
    state.claims = claims as SessionClaims;
    refreshState.warningMinutes = null;
    return isAuthenticated.value;
  } catch {
    state.claims = null;
    refreshState.warningMinutes = null;
    return false;
  }
}

export function clearSession(): void {
  state.claims = null;
  refreshState.warningMinutes = null;
}

/**
 * Store the short-lived account-session token (scope:'account') in memory only.
 * This token is accepted ONLY by POST /auth/select-company; it is NOT the app session.
 * Call clearAccountSession() immediately after calling selectCompany() successfully.
 */
export function setAccountSession(token: string): void {
  accountSessionToken = token;
  try {
    sessionStorage.setItem(ACCOUNT_SESSION_STORAGE_KEY, token);
  } catch {
    // sessionStorage unavailable (private-browsing lockdown, etc.) — the
    // in-memory variable still covers the common case.
  }
}

/** Remove the transient account-session token without affecting the tenant session. */
export function clearAccountSession(): void {
  accountSessionToken = null;
  try {
    sessionStorage.removeItem(ACCOUNT_SESSION_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Returns the account-session token (memory, falling back to sessionStorage), or null. */
export function getAccountSession(): string | null {
  if (accountSessionToken) return accountSessionToken;
  try {
    return sessionStorage.getItem(ACCOUNT_SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Reactive: authenticated == present, non-expired claims (scope-agnostic). */
export const isAuthenticated: ComputedRef<boolean> = computed(
  () => state.claims !== null && !isExpired(state.claims),
);

/** Reactive: current decoded claims (or null). Union of TenantClaims | PlatformClaims. */
export const currentClaims: ComputedRef<SessionClaims | null> = computed(() => state.claims);

/** Reactive: the scope discriminant ('tenant' | 'platform' | null). */
export const currentScope: ComputedRef<SessionScope | null> = computed(
  () => state.claims?.scope ?? null,
);

// ---------------------------------------------------------------------------
// Silent session refresh (session bug fix, 2026-07-21)
//
// PROBLEM: JWT_EXPIRES_IN is a fixed 1h TTL with no renewal mechanism — an
// active user got logged out mid-work with the cut feeling abrupt (no prior
// warning, only detected on the next failed request/navigation).
//
// FIX: a timer polls every 60s while the session is authenticated and someone
// was active in the last IDLE_LIMIT_MS (any tab; see the idle timeout below). Once the token has ≤REFRESH_THRESHOLD_SECONDS left, it calls
// POST /auth/refresh proactively (see api.ts) to re-mint the token WHILE it's
// still valid. If that fails (network down, or the session was genuinely
// revoked server-side), `expiryWarningMinutes` is set so the UI can show
// "tu sesión expira en X minutos" instead of cutting the user off in silence.
// Never attempts to preserve unsaved form state — the goal is that an active
// user practically never reaches expiry in the first place.
// ---------------------------------------------------------------------------

const REFRESH_THRESHOLD_SECONDS = 600; // start trying to refresh 10 min before exp

const refreshState = reactive<{ warningMinutes: number | null }>({ warningMinutes: null });

/** Reactive: minutes left before expiry once a refresh attempt has failed — null when there's nothing to warn about. */
export const sessionExpiryWarningMinutes: ComputedRef<number | null> = computed(
  () => refreshState.warningMinutes,
);

let refreshTimer: ReturnType<typeof setInterval> | undefined;
let refreshInFlight = false;

async function refreshTick(): Promise<void> {
  if (refreshInFlight) return;
  if (!isAuthenticated.value || state.claims === null) return;
  // Bank model (S3, 2026-11-01): renew only while someone is there. The token
  // is renewed in hidden tabs too (the user may be working in another RigX
  // tab, or away for less than the idle limit), but never once nobody has
  // touched any tab for IDLE_LIMIT_MS — then the session is ending anyway.
  syncSharedActivity();
  if (Date.now() - lastActivityAt >= IDLE_LIMIT_MS) return;

  const secondsLeft = state.claims.exp - Math.floor(Date.now() / 1000);
  if (secondsLeft > REFRESH_THRESHOLD_SECONDS) {
    refreshState.warningMinutes = null;
    return;
  }

  refreshInFlight = true;
  try {
    const refreshed = await refreshSession();
    state.claims = refreshed as SessionClaims;
    refreshState.warningMinutes = null;
  } catch {
    // Refresh failed (network error, or the session was revoked server-side) —
    // warn instead of cutting the user off without notice. isAuthenticated
    // still reflects the un-refreshed token's real exp, so the eventual
    // 401→login redirect (router guard) still happens on its own if this
    // never recovers.
    refreshState.warningMinutes = Math.max(0, Math.ceil(secondsLeft / 60));
  } finally {
    refreshInFlight = false;
  }
}

// A backgrounded/unfocused tab either pauses or heavily throttles setInterval
// (2026-07-25 gotcha): a tab parked in the background for a long stretch may
// not have refreshed in time (until 2026-11-01 refreshTick skipped hidden
// tabs; throttled timers still run late), and the FIRST
// check after coming back to foreground was whatever the next throttled poll
// tick happened to land on — sometimes seconds after the token had already
// crossed 0 remaining, so the refresh call itself failed (401, already
// expired) and the user saw the "about to expire" warning with no time left
// to react. Checking immediately on 'visibilitychange' closes that gap: the
// moment the tab regains focus, we know right away instead of waiting up to
// 60s (or longer, throttled) for the next poll.
function handleVisibilityChange(): void {
  if (document.visibilityState === 'visible') void refreshTick();
}

/**
 * Starts the silent-refresh timer. Idempotent — safe to call multiple times
 * (e.g. from every login view) since only the first call schedules anything.
 * Call once at app boot (main.ts, after the initial hydrateSession()).
 */
export function startSessionRefreshTimer(): void {
  if (refreshTimer !== undefined) return;
  refreshTimer = setInterval(() => {
    void refreshTick();
  }, 60_000);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }
  void refreshTick();
}

/** Stops the silent-refresh timer. Exposed for tests — never needed in app code. */
export function stopSessionRefreshTimer(): void {
  if (refreshTimer !== undefined) {
    clearInterval(refreshTimer);
    refreshTimer = undefined;
  }
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  }
}

// ---------------------------------------------------------------------------
// Idle timeout ("¿Seguís ahí?") — 2026-07-25.
//
// PROBLEM: the silent refresh above keeps an AUTHENTICATED session alive
// forever as long as the tab stays open — it never checks whether anyone is
// actually there. A forgotten, unlocked tab stays logged in indefinitely.
// Meanwhile the only user-facing message that existed was the refresh-failure
// banner above, which is a technical fallback, not an inactivity signal, and
// gave near-zero reaction time.
//
// FIX: track real user input (mouse/keyboard/touch/scroll) independent of the
// token's own lifecycle. After IDLE_WARNING_MS with no activity, show a modal
// ("¿Seguís ahí?"); any real activity while it's showing dismisses it. If
// nobody answers within IDLE_LOGOUT_GRACE_MS more, `idleTimedOut` flips true —
// the shell (AppShell.vue) owns the actual logout call + redirect (same path
// as the manual "Salir" button), so this module never needs api.ts's logout()
// or the router, keeping the existing session.ts ↔ api.ts import boundary
// (see clearSession()'s doc comment on why clearMe()/router live in the shell).
//
// SHARED ACROSS TABS (2026-10-12, user report: "estaba trabajando y se cerró
// el sistema"): activity used to be tracked per tab, but the session cookie is
// one for all of them. A second RigX tab left untouched timed out after 17
// min and its logout cleared the cookie under the tab being used. Activity
// is now written to localStorage (throttled) and read back by every tab —
// on each idle check, on `storage` events, and once more right before the
// grace period ends — so no tab logs out while the user is active in another.
//
// PASSIVE INPUT DOESN'T ANSWER THE WARNING (same report: "al mover el mouse
// se cierra y no puedo confirmar"): while "¿Seguís ahí?" is up, mousemove/
// scroll/wheel keep the clock fresh but don't close it — a click, a key or
// "Seguir conectado" does.
// ---------------------------------------------------------------------------

// Bank model (S3): the panel closes after 1 hour with no activity (30 min until
// 2026-11-02, owner: "poné 1 hr mientras avise"), warning one minute before.
// Background requests (polling widgets, notifications) are not activity: only
// real input in any RigX tab is.
const IDLE_WARNING_MS = 59 * 60_000; // 59 min of no activity → show the warning
const IDLE_LOGOUT_GRACE_MS = 60_000; // 1 more min unanswered → idleTimedOut
/** No real activity in any tab for this long → the session closes. */
export const IDLE_LIMIT_MS = IDLE_WARNING_MS + IDLE_LOGOUT_GRACE_MS;
const IDLE_CHECK_INTERVAL_MS = 15_000; // how often we check for "went idle"
const IDLE_COUNTDOWN_TICK_MS = 1_000; // countdown display resolution once the warning is up

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'] as const;
/** Deliberate input that answers an open warning; the rest only counts as activity. */
const ANSWERING_EVENTS = new Set<string>(['mousedown', 'keydown', 'touchstart']);

/** localStorage key every tab of this origin shares for "last real activity". */
export const LAST_ACTIVITY_STORAGE_KEY = 'rigx:last-activity';
const ACTIVITY_SHARE_THROTTLE_MS = 5_000;
let lastSharedAt = 0;

function readSharedActivity(): number {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
    const value = raw === null ? NaN : Number(raw);
    return Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
}

function shareActivity(at: number): void {
  if (at - lastSharedAt < ACTIVITY_SHARE_THROTTLE_MS) return;
  lastSharedAt = at;
  try {
    localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(at));
  } catch {
    // Storage unavailable (private mode, quota): this tab still tracks its own activity.
  }
}

/** Adopts another tab's more recent activity; true when it moved this tab's clock. */
function syncSharedActivity(): boolean {
  const shared = readSharedActivity();
  if (shared <= lastActivityAt) return false;
  lastActivityAt = shared;
  return true;
}

let lastActivityAt = Date.now();
let idleCheckTimer: ReturnType<typeof setInterval> | undefined;
let idleCountdownTimer: ReturnType<typeof setInterval> | undefined;
let idleListenersAttached = false;

const idleState = reactive<{ warningVisible: boolean; logoutAt: number | null; timedOut: boolean }>({
  warningVisible: false,
  logoutAt: null,
  timedOut: false,
});

/** now-ish, ticks every second only while the "¿Seguís ahí?" modal is visible — drives the countdown. */
const idleNow = ref(Date.now());

/** Reactive: true once the idle warning modal should be shown. */
export const isIdleWarningVisible: ComputedRef<boolean> = computed(() => idleState.warningVisible);

/** Reactive: seconds left before auto-logout, while the warning is visible — null otherwise. */
export const idleLogoutInSeconds: ComputedRef<number | null> = computed(() => {
  if (idleState.logoutAt === null) return null;
  return Math.max(0, Math.ceil((idleState.logoutAt - idleNow.value) / 1000));
});

/** Reactive: true once the grace period elapsed with no response — the shell should log out. */
export const idleTimedOut: ComputedRef<boolean> = computed(() => idleState.timedOut);

function showIdleWarning(): void {
  idleState.warningVisible = true;
  idleState.logoutAt = lastActivityAt + IDLE_LIMIT_MS;
  idleNow.value = Date.now();
  idleCountdownTimer = setInterval(() => {
    idleNow.value = Date.now();
    if (idleState.logoutAt !== null && idleNow.value >= idleState.logoutAt) {
      // Last check before logging out: the user moved the mouse here without
      // answering, or is working in another tab — either way, someone is there.
      syncSharedActivity();
      if (Date.now() - lastActivityAt < IDLE_WARNING_MS) {
        hideIdleWarning();
        return;
      }
      idleState.timedOut = true;
      hideIdleWarning();
    }
  }, IDLE_COUNTDOWN_TICK_MS);
}

function hideIdleWarning(): void {
  idleState.warningVisible = false;
  idleState.logoutAt = null;
  if (idleCountdownTimer !== undefined) {
    clearInterval(idleCountdownTimer);
    idleCountdownTimer = undefined;
  }
}

function recordActivity(event?: Event): void {
  lastActivityAt = Date.now();
  shareActivity(lastActivityAt);
  // Passive input (moving the mouse, scrolling) keeps the clock fresh but must
  // not close an open warning on its own — only a deliberate click/key does.
  if (idleState.warningVisible && event && !ANSWERING_EVENTS.has(event.type)) return;
  // Also clears a stale timedOut from a PREVIOUS session in this tab: the
  // activity listeners are attached once at boot and stay authoritative
  // across a logout→login cycle (same idempotent lifecycle as
  // startSessionRefreshTimer), but `idleTimedOut` only flips true→false via
  // real activity — logging back in always generates some (typing, a click),
  // so this naturally re-arms idle detection for the new session before the
  // watcher in AppShell.vue could see a stale `true` and never re-fire.
  idleState.timedOut = false;
  if (idleState.warningVisible) hideIdleWarning();
}

function idleCheckTick(): void {
  if (!isAuthenticated.value || idleState.warningVisible) return;
  syncSharedActivity();
  const idleFor = Date.now() - lastActivityAt;
  // Past the whole limit already (the computer slept, the tab was frozen):
  // close now instead of showing a warning with no time left to answer it.
  if (idleFor >= IDLE_LIMIT_MS) idleState.timedOut = true;
  else if (idleFor >= IDLE_WARNING_MS) showIdleWarning();
}

/** Another tab recorded activity: this one is not idle either. */
function handleSharedActivity(e: StorageEvent): void {
  if (e.key !== LAST_ACTIVITY_STORAGE_KEY || !syncSharedActivity()) return;
  if (idleState.warningVisible && Date.now() - lastActivityAt < IDLE_WARNING_MS) hideIdleWarning();
}

function handleIdleVisibilityChange(): void {
  // Background-tab timer throttling (see handleVisibilityChange above) can
  // delay detecting "went idle" too — re-check immediately on refocus instead
  // of waiting up to IDLE_CHECK_INTERVAL_MS for the next (possibly throttled) tick.
  if (document.visibilityState === 'visible') idleCheckTick();
}

/**
 * Explicit "I'm still here" — called by the warning modal's "Seguir conectado"
 * button. Also proactively renews the JWT immediately (we just proved a real
 * person is present, no reason to wait for the next refresh poll).
 */
export function extendIdleSession(): void {
  recordActivity();
  void refreshTick();
}

/**
 * Starts idle-activity tracking. Idempotent. Call once at app boot, after
 * hydrateSession() — mirrors startSessionRefreshTimer()'s lifecycle.
 */
export function startIdleTracking(): void {
  if (idleCheckTimer !== undefined) return;
  lastActivityAt = Date.now();
  idleState.timedOut = false;
  if (typeof window !== 'undefined') {
    idleListenersAttached = true;
    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, recordActivity, { passive: true, capture: true });
    }
    document.addEventListener('visibilitychange', handleIdleVisibilityChange);
    window.addEventListener('storage', handleSharedActivity);
  }
  shareActivity(lastActivityAt);
  idleCheckTimer = setInterval(idleCheckTick, IDLE_CHECK_INTERVAL_MS);
}

/** Stops idle tracking and clears any pending warning. Exposed for tests / logout teardown. */
export function stopIdleTracking(): void {
  if (idleCheckTimer !== undefined) {
    clearInterval(idleCheckTimer);
    idleCheckTimer = undefined;
  }
  hideIdleWarning();
  if (idleListenersAttached && typeof window !== 'undefined') {
    idleListenersAttached = false;
    for (const evt of ACTIVITY_EVENTS) {
      window.removeEventListener(evt, recordActivity, { capture: true });
    }
    document.removeEventListener('visibilitychange', handleIdleVisibilityChange);
    window.removeEventListener('storage', handleSharedActivity);
  }
}

/**
 * Only for tests — resets every module-scope singleton this file owns
 * (claims, account-session token, refresh/idle timers and reactive state)
 * back to its initial value. Never call from app code.
 *
 * This module is a classic "singleton reactive + module-level timers" shape
 * (`state`, `refreshState`, `idleState`, `refreshTimer`, `idleCheckTimer`,
 * `idleCountdownTimer`, `lastActivityAt`, `idleListenersAttached`,
 * `accountSessionToken`) — under `vitest run --no-isolate` these survive
 * across spec FILES (not just across tests within one file), since the
 * module is loaded once per worker instead of once per file. A test in one
 * file calling `startSessionRefreshTimer()`/`startIdleTracking()` left a real
 * `setInterval` running and `state.claims` set, which a LATER file's tests
 * (never having called those themselves) inherited.
 */
export function __resetSessionForTests(): void {
  stopSessionRefreshTimer();
  stopIdleTracking();
  state.claims = null;
  refreshState.warningMinutes = null;
  refreshInFlight = false;
  idleState.warningVisible = false;
  idleState.logoutAt = null;
  idleState.timedOut = false;
  lastActivityAt = Date.now();
  lastSharedAt = 0;
  accountSessionToken = null;
  try {
    sessionStorage.removeItem(ACCOUNT_SESSION_STORAGE_KEY);
    localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
  } catch {
    // ignore
  }
}
